/**
 * React Flow visualisation of an agent set: members as nodes, their
 * `transfer_agent` (solid) and `subagent` (dashed) links as directed edges.
 *
 * Layout + edge routing are delegated to ELK (elkjs) — a proper layered engine
 * with orthogonal edge routing, automatic port assignment and label placement —
 * so arbitrary topologies (branching, converging, shared tools, back-edges) lay
 * out cleanly. Direction is DOWN, so the call hierarchy flows top→bottom and a
 * tool settles one layer below the agent that calls it; voice and text members
 * are told apart by colour (green/amber) and edge style (solid/dashed). ELK runs
 * asynchronously; the graph is computed in an effect and held in state.
 * Read-only; pan/zoom/fit enabled.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Controls, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import Typography from '@mui/joy/Typography';
import AgentNode, { NODE_WIDTH, NODE_HEIGHT } from './AgentNode';
import PhoneNode, { PHONE_NODE_WIDTH, PHONE_NODE_HEIGHT } from './PhoneNode';
import AgentSetEdge, { EDGE_COLOUR } from './AgentSetEdge';
import { buildSetGraph } from './setGraph';

const nodeTypes = { agentNode: AgentNode, phoneNode: PhoneNode };
const edgeTypes = { agentEdge: AgentSetEdge };
const elk = new ELK();

const ELK_OPTS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.edgeRouting': 'ORTHOGONAL',
  // Place every edge label consistently on the LEFT of its (downward) edge,
  // rather than ELK's default heuristic that flips sides to dodge overlaps.
  // (ELK's up/down naming is relative to its internal orientation: with
  // direction DOWN, ALWAYS_DOWN resolves to the left-hand side.)
  'elk.layered.edgeLabels.sideSelection': 'ALWAYS_DOWN',
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.spacing.nodeNode': '45',
  'elk.layered.spacing.nodeNodeBetweenLayers': '95',
  'elk.spacing.edgeNode': '24',
  'elk.spacing.edgeEdge': '14',
  'elk.spacing.edgeLabel': '6',
  'elk.layered.spacing.edgeEdgeBetweenLayers': '14',
};

const labelWidth = (text) => text.length * 6 + 12;

function styleEdges(edges) {
  return edges.map((e) => {
    const colour = EDGE_COLOUR[e.data?.kind] || EDGE_COLOUR.transfer;
    const subagent = e.data?.kind === 'subagent';
    const marker = { type: MarkerType.ArrowClosed, color: colour, width: 16, height: 16 };
    return {
      ...e,
      type: 'agentEdge',
      markerEnd: marker,
      // A subagent call is a round trip (ask → result), so arrow both ends;
      // a transfer is a one-way handover, so leave the source end plain.
      ...(subagent ? { markerStart: marker } : {}),
      style: { stroke: colour, strokeWidth: 1.6, ...(subagent ? { strokeDasharray: '6 4' } : {}) },
    };
  });
}

/**
 * Lay out a set with ELK and map the result back to React Flow nodes/edges.
 * Each edge carries ELK's orthogonal route as `data.points` and its placed
 * label as `data.routeLabelAt` (AgentSetEdge renders both). Async.
 */
export async function computeElkGraph(set) {
  const model = buildSetGraph(set);
  if (!model.nodes.length) return { nodes: [], edges: [] };

  const elkGraph = {
    id: 'root',
    layoutOptions: ELK_OPTS,
    children: model.nodes.map((n) => {
      const phone = n.type === 'phoneNode';
      return {
        id: String(n.id),
        width: phone ? PHONE_NODE_WIDTH : NODE_WIDTH,
        height: phone ? PHONE_NODE_HEIGHT : NODE_HEIGHT,
      };
    }),
    edges: model.edges.map((e) => ({
      id: String(e.id),
      sources: [String(e.source)],
      targets: [String(e.target)],
      // Phone edges carry no mid-edge chip: the endpoint node already shows the
      // transfer name, so a label here would just duplicate it.
      ...(e.data?.functionName && e.data?.kind !== 'phone'
        ? {
          labels: [{
            id: `${e.id}__label`,
            text: e.data.functionName,
            width: labelWidth(e.data.functionName),
            height: 18,
            layoutOptions: { 'elk.edgeLabels.placement': 'CENTER' },
          }],
        }
        : {}),
    })),
  };

  const laid = await elk.layout(elkGraph);

  const posById = new Map((laid.children || []).map((c) => [c.id, c]));
  const nodes = model.nodes.map((n) => {
    const c = posById.get(String(n.id));
    return { ...n, position: { x: c?.x ?? 0, y: c?.y ?? 0 } };
  });

  const laidById = new Map((laid.edges || []).map((e) => [e.id, e]));
  const edges = model.edges.map((e) => {
    const le = laidById.get(String(e.id));
    const sec = le?.sections?.[0];
    const points = sec ? [sec.startPoint, ...(sec.bendPoints || []), sec.endPoint] : [];
    const lab = le?.labels?.[0];
    const routeLabelAt = lab ? { x: lab.x + lab.width / 2, y: lab.y + lab.height / 2 } : undefined;
    return {
      ...e,
      // Valid handles keep reactflow happy; the explicit ELK `points` drive the visual.
      sourceHandle: 'r',
      targetHandle: 'l',
      data: { ...e.data, points, routeLabel: e.data?.functionName, routeLabelAt },
    };
  });

  return { nodes, edges: styleEdges(edges) };
}

export default function AgentSetGraph({ set, height = 540, onSelectAgent }) {
  const [graph, setGraph] = useState({ nodes: [], edges: [], ready: false });

  useEffect(() => {
    let alive = true;
    setGraph((g) => ({ ...g, ready: false }));
    computeElkGraph(set)
      .then((g) => { if (alive) setGraph({ ...g, ready: true }); })
      .catch((err) => {
        // A draft/partial set streamed mid-build can be malformed; degrade to an
        // empty graph rather than throwing and losing the surrounding UI.
        // eslint-disable-next-line no-console
        console.error('AgentSetGraph: layout failed', err);
        if (alive) setGraph({ nodes: [], edges: [], ready: true });
      });
    return () => { alive = false; };
  }, [set]);

  // Flag clickable nodes (cheap; kept out of the async layout so a changing
  // onSelectAgent identity doesn't re-run ELK). A node is clickable when it's a
  // real member of this set — not an external reference, a phone endpoint, or an
  // unsaved label-only draft. Voice members are additionally browser-testable;
  // text members can only be edited.
  const nodes = useMemo(() => (onSelectAgent
    ? graph.nodes.map((n) => {
      const d = n.data || {};
      const id = String(n.id);
      const clickable = !d.external && n.type !== 'phoneNode'
        && !id.startsWith('label:') && !id.startsWith('ext:');
      return clickable
        ? { ...n, data: { ...d, clickable: true, testable: d.agentType !== 'text' } }
        : n;
    })
    : graph.nodes), [graph.nodes, onSelectAgent]);

  const onNodeClick = useCallback((_e, node) => {
    const d = node.data || {};
    const id = String(node.id);
    const clickable = !d.external && node.type !== 'phoneNode'
      && !id.startsWith('label:') && !id.startsWith('ext:');
    if (onSelectAgent && clickable) {
      onSelectAgent({ id, name: d.name, agentType: d.agentType, testable: d.agentType !== 'text' });
    }
  }, [onSelectAgent]);

  if (graph.ready && !nodes.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
          This set has no agents to display.
        </Typography>
      </Box>
    );
  }

  if (!nodes.length) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%', height, borderRadius: 'sm', overflow: 'hidden',
      // Edge-label chips render in reactflow's edgelabel layer, which sits BELOW
      // nodes by default — so a label next to a node was painted over and hidden.
      // Lift the layer above nodes. It's pointer-events:none, so node clicks still
      // pass through; and it stays under the Controls (a separate, higher
      // stacking slot: .react-flow__panel z5 > .react-flow__renderer z4).
      '& .react-flow__edgelabel-renderer': { zIndex: 1002 },
    }}>
      <ReactFlow
        nodes={nodes}
        edges={graph.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={18} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
}
